import { FocusTrap } from 'vendor';

const STORAGE_KEY = 'blossholm:fullwidth-popup-appeared';

class FullwidthPopup extends HTMLElement {
  static get observedAttributes() {
    return ['open'];
  }

  #hasTriggered = false;
  #delayTimer = null;
  #scrollHandler = null;
  #focusTrap = null;
  #keydownHandler = null;
  #sectionSelectHandler = null;
  #sectionDeselectHandler = null;

  connectedCallback() {
    this.querySelector('.fullwidth-popup__close')?.addEventListener('click', () => this.hide());
    this.querySelector('.fullwidth-popup__overlay')?.addEventListener('click', () => this.hide());
    this.querySelector('.fullwidth-popup__dismiss')?.addEventListener('click', () => this.hide());

    this.#keydownHandler = (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) {
        this.hide();
      }
    };
    document.addEventListener('keydown', this.#keydownHandler);

    // Theme editor support
    this.#sectionSelectHandler = (e) => {
      if (e.target === this.closest('.shopify-section')) this.show();
    };
    document.addEventListener('shopify:section:select', this.#sectionSelectHandler);

    this.#sectionDeselectHandler = (e) => {
      if (e.target === this.closest('.shopify-section')) this.hide();
    };
    document.addEventListener('shopify:section:deselect', this.#sectionDeselectHandler);

    if (this.#shouldAppearAutomatically()) {
      this.#setupTriggers();
    }
  }

  disconnectedCallback() {
    this.#clearTriggers();
    document.removeEventListener('keydown', this.#keydownHandler);
    document.removeEventListener('shopify:section:select', this.#sectionSelectHandler);
    document.removeEventListener('shopify:section:deselect', this.#sectionDeselectHandler);
  }

  attributeChangedCallback(name) {
    if (name !== 'open') return;

    if (this.hasAttribute('open')) {
      this.removeAttribute('inert');
      document.documentElement.classList.add('lock');
      if (this.hasAttribute('data-only-once')) {
        localStorage.setItem(STORAGE_KEY, 'true');
      }
      this.#activateFocusTrap();
    } else {
      this.setAttribute('inert', '');
      document.documentElement.classList.remove('lock');
      this.#deactivateFocusTrap();
    }
  }

  show() {
    this.setAttribute('open', '');
  }

  hide() {
    this.removeAttribute('open');
  }

  get delayTrigger() {
    return parseInt(this.dataset.delayTrigger || '0', 10);
  }

  get scrollTrigger() {
    return parseInt(this.dataset.scrollTrigger || '0', 10);
  }

  #shouldAppearAutomatically() {
    if (this.hasAttribute('data-only-once') && localStorage.getItem(STORAGE_KEY) === 'true') {
      return false;
    }
    return true;
  }

  #setupTriggers() {
    const delay = this.delayTrigger;
    const scrollPct = this.scrollTrigger;

    if (delay > 0) {
      this.#delayTimer = setTimeout(() => this.#trigger(), delay * 1000);
    }

    if (scrollPct > 0) {
      this.#scrollHandler = () => {
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) return;
        const scrolled = (window.scrollY / docHeight) * 100;
        if (scrolled >= scrollPct) this.#trigger();
      };
      window.addEventListener('scroll', this.#scrollHandler, { passive: true });
    }
  }

  #trigger() {
    if (this.#hasTriggered) return;
    this.#hasTriggered = true;
    this.#clearTriggers();
    this.show();
  }

  #clearTriggers() {
    if (this.#delayTimer) {
      clearTimeout(this.#delayTimer);
      this.#delayTimer = null;
    }
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }
  }

  #activateFocusTrap() {
    if (!this.#focusTrap) {
      this.#focusTrap = FocusTrap.createFocusTrap([this], {
        fallbackFocus: this,
        allowOutsideClick: true,
        preventScroll: true,
      });
    }
    this.#focusTrap.activate();
  }

  #deactivateFocusTrap() {
    this.#focusTrap?.deactivate();
  }
}

if (!customElements.get('fullwidth-popup')) {
  customElements.define('fullwidth-popup', FullwidthPopup);
}
